import { DataTable } from '@/components/data-table/DataTable';
import { TableHeaderWithSort } from '@/components/data-table/TableHeaderWithSort';
import ConfirmationModal from '@/components/modal/ConfirmationModal';
import { useModal } from '@/components/modal/useModal';
import { LpohPagination } from '@/components/pagination/LpohPagination';
import { PageSize } from '@/components/pagination/PageSize';
import { Badge, badgeVariants } from '@/components/ui/badge';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ROUTES } from '@/constants';
import useResetTableStateOnChange from '@/hooks/useResetTableState';
import { PAGE_SIZES } from '@/routes/measurement-group/list/constants';
import { DEFAULT_DATE_TIME_FORMAT } from '@/utils/dates';
import { getUIErrorMessage } from '@/utils/errors';
import { Params } from '@/utils/types';
import { ChevronDownIcon, ChevronRightIcon, DotsVerticalIcon, ValueNoneIcon } from '@radix-ui/react-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ColumnDef, ExpandedState } from '@tanstack/react-table';
import { AxiosResponse } from 'axios';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';
import CalibrationTaskDetails from './CalibrationTaskDetails';
import {
	DEFAULT_PAGE_SIZE,
	deleteTaskFiles,
	listCalibrationTasks,
	retryTask,
	revokeTask,
	taskStatusLabels,
	taskTypeLabels
} from './constants';
import DownloadResults from './DownloadResults';
import TasksFilters from './TasksFilters';
import { CalibrationsTaskStatus, CalibrationTaskListResponse, Task } from './types';
import useTasksFilters from './useTasksFilters';
import { BreadcrumbRouterLink } from '@/components/link/Link';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuPortal,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { VariantProps } from 'class-variance-authority';
import { toast } from 'sonner';
import CopyToClipboard from '@/components/copy-to-clipboard/CopyToClipboard';
import useTitle from '@/hooks/useTitle.ts';

const taskStatusToBadgeVariant: Record<CalibrationsTaskStatus, VariantProps<typeof badgeVariants>['variant']> = {
	SUCCESS: 'success',
	REQUESTED: 'neutral',
	REQUEST_FAILED: 'error',
	REQUEST_CANCELED: 'error',
	FAILURE: 'error',
	PENDING: 'warn',
	CANCELED: 'error',
	DESYNCHRONIZED: 'neutral',
	STARTED: 'neutral'
};

export default function CalibrationTasks() {
	useTitle("Tasks");

	const initialData = useLoaderData() as AxiosResponse<CalibrationTaskListResponse>;
	const { closeModal: closeRevokeTaskModal, isOpen: isRevokeTaskOpen, openModal: openRevokeTaskModal } = useModal();
	const {
		closeModal: closeDeleteFilesModal,
		isOpen: isDeleteFilesOpen,
		openModal: openDeleteFilesModal
	} = useModal();
	const { closeModal: closeRetryTaskModal, isOpen: isRetryTaskOpen, openModal: openRetryTaskModal } = useModal();
	const [searchParams] = useSearchParams();

	const page = searchParams.get(Params.page) || '1';
	const limit = searchParams.get(Params.pageSize) || DEFAULT_PAGE_SIZE;
	const sortBy = searchParams.get(Params.sortBy) || '';
	const sortDirection = searchParams.get(Params.sortDirection) || '';
	const mgId = searchParams.get(Params.mgId) || '';
	const type = searchParams.get(Params.type) || '';
	const status = searchParams.get(Params.status) || '';
	const [autoRefresh, setAutoRefresh] = useState(false);

	const {
		data: calibrationTaskList,
		refetch,
		isPending: isTasksListRefetching
	} = useQuery({
		...listCalibrationTasks({ page, limit, sortBy, sortDirection, mgId, type, status }),
		initialData,
		refetchInterval: autoRefresh ? 5000 : 0,
		enabled: autoRefresh
	});

	const { items, totalPages } = calibrationTaskList.data;

	const [expanded, setExpanded] = useState<ExpandedState>({});
	const [selectedCalibrationTaskId, setSelectedCalibrationTaskId] = useState('');
	const [modalError, setModalError] = useState('');

	const { handleMgIdChange, handleTaskStatusChange, handleTaskTypeChange } = useTasksFilters();

	const getExpandedRowContent = (row: Task) => {
		return <CalibrationTaskDetails task={row} />;
	};

	const { mutate: stopCalibrationTask, isPending: isCancelling } = useMutation({
		mutationFn: revokeTask,
		onSuccess: () => {
			handleCloseRevokeTaskModal();
			toast('Task revoked successfully.');
			refetch();
		},
		onError: error => {
			setModalError(getUIErrorMessage(error));
		}
	});

	const { mutate: retryCalibrationTask, isPending: isRetrying } = useMutation({
		mutationFn: retryTask,
		onSuccess: () => {
			handleCloseRetryTaskModal();
			toast('Task retried successfully.');
			refetch();
		},
		onError: error => {
			setModalError(getUIErrorMessage(error));
		}
	});

	const { mutate: deleteFiles, isPending: isDeletingFiles } = useMutation({
		mutationFn: deleteTaskFiles,
		onSuccess: () => {
			handleCloseDeleteFilesModal();
			toast('Task files deleted successfully.');
			refetch();
		},
		onError: error => {
			setModalError(getUIErrorMessage(error));
		}
	});

	useResetTableStateOnChange({
		observed: { page, limit, sortBy, sortDirection, mgId, type, status },
		callbackFn: () => setExpanded({})
	});

	const handleCloseRevokeTaskModal = () => {
		closeRevokeTaskModal();
		setModalError('');
	};

	const handleCloseDeleteFilesModal = () => {
		closeDeleteFilesModal();
		setModalError('');
	};

	const handleCloseRetryTaskModal = () => {
		closeRetryTaskModal();
		setModalError('');
	};

	const taskHasFiles = (task: Task) => {
		return (
			task.fileInitialCalibration ||
			task.fileUpdatedCalibration ||
			task.fileHistoricalData ||
			task.fileReferenceData ||
			task.fileRevisionData
		);
	};

	const taskIsInProgress = (task: Task) => {
		return (
			task.status === CalibrationsTaskStatus.STARTED ||
			task.status === CalibrationsTaskStatus.PENDING ||
			task.status === CalibrationsTaskStatus.REQUESTED
		);
	};

	const columns: ColumnDef<Task>[] = useMemo(
		() => [
			{
				id: 'expand',
				cell: ({ row }) => (
					<Button onClick={() => row.toggleExpanded()} variant="ghost" size="icon" title="Expand row">
						{row.getIsExpanded() ? <ChevronDownIcon /> : <ChevronRightIcon />}
					</Button>
				)
			},
			{
				accessorKey: 'id',
				header: 'Task ID',
				cell: ({ row }) => (
					<div className="flex gap-2 justify-between items-center" title={row.original.id}>
						<div className="max-w-[60px] truncate">{row.original.id}</div>
						{window.isSecureContext && <CopyToClipboard textToCopy={row.original.id} />}
					</div>
				)
			},

			{
				accessorKey: 'measurementGroupId',
				header: ({ column }) => <TableHeaderWithSort column={column} columnName="Measurement Group ID" />
			},
			{
				accessorKey: 'measurementGroupName',
				header: 'Measurement Group Name'
			},
			{
				accessorKey: 'type',
				header: ({ column }) => <TableHeaderWithSort column={column} columnName="Type" />,
				cell: ({ row }) => (
					<Badge className="min-w-40 justify-center" variant="neutral">
						{taskTypeLabels[row.original.type]}
					</Badge>
				)
			},
			{
				accessorKey: 'status',
				header: ({ column }) => <TableHeaderWithSort column={column} columnName="Status" />,
				cell: ({ row }) => (
					<Badge className="min-w-36 justify-center" variant={taskStatusToBadgeVariant[row.original.status]}>
						{taskStatusLabels[row.original.status]}
					</Badge>
				)
			},
			{
				accessorKey: 'submissionDate',
				header: ({ column }) => <TableHeaderWithSort column={column} columnName="Submission Date" />,
				cell: ({ row }) => format(new Date(row.original.requestTime), DEFAULT_DATE_TIME_FORMAT)
			},
			{
				header: 'Download Results',
				cell: ({ row }) =>
					row.original.filesDeletedAt ? (
						<div title="Files deleted by user" className=" w-full flex justify-start px-4">
							<ValueNoneIcon />
						</div>
					) : (
						<DownloadResults row={row} />
					)
			},
			{
				header: 'Actions',
				cell: ({ row }) => (
					<DropdownMenu modal={false}>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="default" title="Actions menu">
								<DotsVerticalIcon className="h-[1.2rem] w-[1.2rem]" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuPortal>
							<DropdownMenuContent align="start">
								<div className="flex flex-col gap-2 items-start">
									<Button
										variant="link"
										className="hover:no-underline"
										title="Revoke task"
										aria-label="Revoke task"
										disabled={
											row.original.status !== CalibrationsTaskStatus.STARTED &&
											row.original.status !== CalibrationsTaskStatus.PENDING
										}
										onClick={() => {
											setSelectedCalibrationTaskId(row.original.id);
											openRevokeTaskModal();
										}}
									>
										Revoke task
									</Button>

									<Button
										variant="link"
										className="hover:no-underline"
										title="Delete task files"
										aria-label="Delete task files"
										disabled={taskIsInProgress(row.original) || !taskHasFiles(row.original)}
										onClick={() => {
											setSelectedCalibrationTaskId(row.original.id);
											openDeleteFilesModal();
										}}
									>
										Delete task files
									</Button>

									<Button
										variant="link"
										className="hover:no-underline"
										title="Retry task"
										aria-label="Retry task"
										onClick={() => {
											setSelectedCalibrationTaskId(row.original.id);
											openRetryTaskModal();
										}}
									>
										Retry task
									</Button>
								</div>
							</DropdownMenuContent>
						</DropdownMenuPortal>
					</DropdownMenu>
				)
			}
		],
		[openRevokeTaskModal, openDeleteFilesModal, openRetryTaskModal]
	);

	return (
		<div className="flex flex-col gap-10">
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbRouterLink to={ROUTES.home}>Home</BreadcrumbRouterLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />

					<BreadcrumbItem>
						<BreadcrumbPage>Tasks</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>
			<div className="w-full flex gap-5 flex-col">
				<div className="w-full flex flex-col-reverse gap-5 md:flex-row md:justify-between">
					<TasksFilters
						handleMgIdChange={handleMgIdChange}
						handleTaskStatusChange={handleTaskStatusChange}
						handleTaskTypeChange={handleTaskTypeChange}
					/>
					<div className="flex items-center space-x-2">
						<Switch
							id="auto-refresh"
							checked={autoRefresh}
							onCheckedChange={() => setAutoRefresh(!autoRefresh)}
						/>
						<Label htmlFor="auto-refresh">Auto refresh</Label>
					</div>
				</div>
				<DataTable
					columns={columns}
					data={items}
					expanded={expanded}
					setExpanded={setExpanded}
					getExpandedRowContent={getExpandedRowContent}
				/>

				<div className="flex justify-between">
					<PageSize pageSize={limit} options={PAGE_SIZES} />
					<LpohPagination currentPage={parseInt(page)} totalPages={totalPages} />
				</div>
				<ConfirmationModal
					isOpen={isRevokeTaskOpen}
					title="Are you sure?"
					body="This action will prevent the calibration task from being completed and the results will not be available."
					handleClose={handleCloseRevokeTaskModal}
					handleConfirm={() => stopCalibrationTask(selectedCalibrationTaskId)}
					isDisabled={isCancelling || isTasksListRefetching}
					errorMessage={modalError}
					isLoading={isCancelling}
				/>

				<ConfirmationModal
					isOpen={isDeleteFilesOpen}
					title="Are you sure?"
					body="Do you want to delete the files associated with this task. This action cannot be undone."
					handleClose={handleCloseDeleteFilesModal}
					handleConfirm={() => deleteFiles(selectedCalibrationTaskId)}
					errorMessage={modalError}
					isDisabled={isDeletingFiles || isTasksListRefetching}
					isLoading={isDeletingFiles}
				/>

				<ConfirmationModal
					isOpen={isRetryTaskOpen}
					title="Are you sure?"
					body="This action will create a new task with the same parameters as the one do you want to retry."
					handleClose={handleCloseRetryTaskModal}
					handleConfirm={() => retryCalibrationTask(selectedCalibrationTaskId)}
					isDisabled={isRetrying || isTasksListRefetching}
					errorMessage={modalError}
					isLoading={isRetrying}
				/>
			</div>
		</div>
	);
}
